/**
 * features/finance/adjustmentWrite.ts
 * ------------------------------------------------------------------
 * Writing a credit note or a write-off.
 *
 * Two things move together and must not come apart:
 *
 *   1. the adjustment record itself, numbered from a counter, and
 *   2. the invoice's `adjusted` / `balance` / `status`.
 *
 * Both happen inside ONE transaction. Written separately, a failure between
 * them leaves either a credit note nobody applied or an invoice reduced by a
 * credit note that does not exist — and the second is money the school has
 * silently given away.
 *
 * WHAT IS NOT HERE YET. The source this is ported from also posts a journal
 * (Dr income or bad debts, Cr receivables) and, for a refund, raises a payment
 * voucher. Neither the ledger nor vouchers exist on this system yet — they are
 * blocks 3 and 4 — so:
 *
 *   - the journal is deliberately absent. lib/adjustments already knows which
 *     accounts an adjustment hits (`adjustmentLines`), tested, waiting for
 *     something to post them. Wiring the posting is the whole of the work when
 *     the ledger lands.
 *   - REFUNDS ARE NOT SUPPORTED. A refund is money going back to a family, and
 *     without a payment voucher there is nothing to move it. Recording one
 *     here would reduce a balance and hand over no cash — the family would be
 *     told they had been refunded when they had not. Better to refuse the
 *     whole kind than to half-do it.
 *
 * Every write is audited.
 * ------------------------------------------------------------------ */
import { collection, doc, getDocs, query, runTransaction, where } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import {
  FINANCE_COLLECTIONS, invoiceStatus, round2, type Invoice,
} from '@/lib/financeCore';
import {
  ADJUSTMENT_LABEL, adjustableRemaining, adjustmentCounterId,
  assertAdjustmentAmount, formatAdjustmentNo,
  type Adjustment, type AdjustmentKind,
} from '@/lib/adjustments';
import { auditFromContext } from '@/lib/audit';

/**
 * The financial year an adjustment falls in.
 *
 * LKC has no financial-year record yet — that arrives with the ledger — but
 * the field is part of the adjustment and the ledger will group on it, so it
 * is stamped now rather than backfilled later. The school's year runs with the
 * calendar, so the year of the date is the whole of it.
 */
const fyForDate = (iso: string): string => `FY${String(iso).slice(0, 4)}`;

/** Kinds this system can honour today. A refund needs a payment voucher. */
export const SUPPORTED_KINDS: AdjustmentKind[] = ['credit_note', 'write_off'];

export interface NewAdjustmentInput {
  kind: AdjustmentKind;
  invoice: Pick<Invoice, 'id'>;
  amount: number;
  reason: string;
  date: string;
  actor: { uid?: string; name?: string; email?: string; displayName?: string };
  /**
   * The income account this credit note reverses out of, named by SYSTEM KEY
   * rather than by code so it survives the school renumbering its chart.
   *
   * A concession passes 'DISCOUNTS_GRANTED' so it lands in contra-revenue
   * (4900) instead of reversing tuition. The difference shows at year end:
   * reversing tuition understates what the school billed and hides what it
   * gave away; 4900 keeps gross fee income intact and presents the
   * concessions as the deduction from revenue they are.
   *
   * Absent means the posting picks the account when the ledger triggers land.
   */
  incomeAccountKey?: string;
}

/** Every adjustment recorded against one invoice, newest first. */
export async function fetchInvoiceAdjustments(invoiceId: string): Promise<Adjustment[]> {
  const snap = await getDocs(query(
    collection(db, FINANCE_COLLECTIONS.adjustments),
    where('invoiceId', '==', invoiceId),
  ));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }) as Adjustment)
    .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
}

export async function createAdjustment(input: NewAdjustmentInput): Promise<Adjustment> {
  if (!SUPPORTED_KINDS.includes(input.kind)) {
    throw new Error(
      'Refunds need a payment voucher to move the money, which this system does not have yet. '
      + 'Record the repayment through the normal payment screens instead.',
    );
  }
  const amount = assertAdjustmentAmount(input.amount);
  const reason = String(input.reason ?? '').trim();
  if (reason.length < 3) {
    // The reason is the whole audit value of an adjustment. A credit note with
    // no explanation is indistinguishable from an error six months later.
    throw new Error('Give a reason. It is the record of why the school gave this up.');
  }

  const year = String(input.date).slice(0, 4);
  const at = new Date().toISOString();
  const ref = doc(collection(db, FINANCE_COLLECTIONS.adjustments));
  const counterRef = doc(db, FINANCE_COLLECTIONS.counters, adjustmentCounterId(year));
  const invoiceRef = doc(db, FINANCE_COLLECTIONS.invoices, input.invoice.id);

  const adjustment = await runTransaction(db, async (tx) => {
    const invSnap = await tx.get(invoiceRef);
    if (!invSnap.exists()) throw new Error('That invoice no longer exists.');
    const live = { id: invSnap.id, ...invSnap.data() } as Invoice;
    if (live.status === 'void') throw new Error('That invoice is void. There is nothing left to adjust.');

    const alreadyAdjusted = round2(Number(live.adjusted) || 0);

    /* You cannot credit or write off more than is actually outstanding.
       Doing so pushes the invoice negative, which reads as the school owing a
       family money it never received. A genuine overpayment is a refund. */
    const room = adjustableRemaining({ total: live.total, paid: live.paid }, alreadyAdjusted);
    if (amount > room + 0.005) {
      throw new Error(
        room <= 0
          ? 'Nothing is outstanding on this invoice. An overpayment is refunded, not credited.'
          : `Only ${room.toFixed(2)} is outstanding on this invoice.`,
      );
    }

    // The number comes from a counter inside the same transaction, so two
    // people crediting at once cannot be handed the same one.
    const counterSnap = await tx.get(counterRef);
    const seq = (counterSnap.exists() ? Number(counterSnap.data().seq) || 0 : 0) + 1;
    const adjustmentNo = formatAdjustmentNo(seq, year);

    const nextAdjusted = round2(alreadyAdjusted + amount);

    const docData: Adjustment = {
      id: ref.id,
      adjustmentNo,
      kind: input.kind,
      studentId: live.studentId,
      ...(live.studentName ? { studentName: live.studentName } : {}),
      ...(live.familyId ? { familyId: live.familyId } : {}),
      invoiceId: live.id,
      invoiceNo: live.invoiceNo,
      fy: fyForDate(input.date),
      amount,
      reason,
      date: input.date,
      // Stamped now so the journal is already decided when posting lands.
      ...(input.incomeAccountKey ? { incomeAccountKey: input.incomeAccountKey } : {}),
      createdBy: input.actor.uid,
      ...(input.actor.name ? { createdByName: input.actor.name } : {}),
      createdAt: at,
    } as Adjustment;

    tx.set(counterRef, { seq, updatedAt: at }, { merge: true });
    tx.set(ref, docData);
    tx.update(invoiceRef, {
      adjusted: nextAdjusted,
      balance: round2(live.total - live.paid - nextAdjusted),
      status: invoiceStatus(live.total, live.paid, false, nextAdjusted),
      updatedAt: at,
    });

    return docData;
  });

  auditFromContext(input.actor as never, `adjustment_${input.kind}`, 'finance', {
    targetId: adjustment.studentId,
    targetType: 'student',
    targetName: adjustment.studentName,
    details: {
      adjustmentNo: adjustment.adjustmentNo,
      kind: ADJUSTMENT_LABEL[adjustment.kind],
      invoiceNo: adjustment.invoiceNo,
      amount: adjustment.amount,
      reason: adjustment.reason,
    },
  });

  return adjustment;
}

/**
 * Reverse an adjustment.
 *
 * The original is never deleted or edited — it happened, and a credit note
 * that vanishes is exactly what an audit trail exists to prevent. A reversal
 * is a second record pointing at the first, and the invoice is put back.
 */
export async function reverseAdjustment(
  original: Adjustment,
  reason: string,
  actor: NewAdjustmentInput['actor'],
): Promise<Adjustment> {
  const why = String(reason ?? '').trim();
  if (why.length < 3) throw new Error('Give a reason for reversing it.');

  const year = String(original.date ?? original.createdAt).slice(0, 4);
  const at = new Date().toISOString();
  const ref = doc(collection(db, FINANCE_COLLECTIONS.adjustments));
  const counterRef = doc(db, FINANCE_COLLECTIONS.counters, adjustmentCounterId(year));
  const originalRef = doc(db, FINANCE_COLLECTIONS.adjustments, original.id);
  // invoiceId is optional on the type. An adjustment with no invoice behind it
  // has no balance to put back, so there is simply nothing to update.
  const invoiceRef = original.invoiceId
    ? doc(db, FINANCE_COLLECTIONS.invoices, original.invoiceId)
    : null;

  const reversal = await runTransaction(db, async (tx) => {
    const origSnap = await tx.get(originalRef);
    if (!origSnap.exists()) throw new Error('That adjustment no longer exists.');
    if ((origSnap.data() as Adjustment).reversedBy) throw new Error('That adjustment has already been reversed.');

    const invSnap = invoiceRef ? await tx.get(invoiceRef) : null;
    const counterSnap = await tx.get(counterRef);
    const seq = (counterSnap.exists() ? Number(counterSnap.data().seq) || 0 : 0) + 1;
    const adjustmentNo = formatAdjustmentNo(seq, year);

    const docData = {
      id: ref.id,
      adjustmentNo,
      kind: original.kind,
      studentId: original.studentId,
      ...(original.studentName ? { studentName: original.studentName } : {}),
      ...(original.familyId ? { familyId: original.familyId } : {}),
      invoiceId: original.invoiceId,
      invoiceNo: original.invoiceNo,
      fy: fyForDate(at),
      amount: original.amount,
      reason: why,
      date: at.slice(0, 10),
      reversesId: original.id,
      createdBy: actor.uid,
      ...(actor.name ? { createdByName: actor.name } : {}),
      createdAt: at,
    } as Adjustment;

    tx.set(counterRef, { seq, updatedAt: at }, { merge: true });
    tx.set(ref, docData);
    tx.update(originalRef, { reversedBy: ref.id, reversedAt: at });

    if (invoiceRef && invSnap?.exists()) {
      const live = { id: invSnap.id, ...invSnap.data() } as Invoice;
      const nextAdjusted = Math.max(0, round2((Number(live.adjusted) || 0) - original.amount));
      tx.update(invoiceRef, {
        adjusted: nextAdjusted,
        balance: round2(live.total - live.paid - nextAdjusted),
        status: invoiceStatus(live.total, live.paid, live.status === 'void', nextAdjusted),
        updatedAt: at,
      });
    }

    return docData;
  });

  auditFromContext(actor as never, 'adjustment_reversed', 'finance', {
    targetId: original.studentId,
    targetType: 'student',
    targetName: original.studentName,
    details: {
      reverses: original.adjustmentNo,
      reversalNo: reversal.adjustmentNo,
      amount: original.amount,
      reason: why,
    },
  });

  return reversal;
}
